/**
 * The rule a resource is booked by: how long a booking may run, and when the resource is open.
 *
 * A leaf module, like the resolver that reads these rows — it depends on nothing but zod and the time
 * frames, so the API, the dashboard and the tests can all reach the guardrail without pulling in the
 * endpoint graph.
 */
import { z } from 'zod';

import { DAILY_RETURN_AFTER_PICKUP_MESSAGE, isDailyReturnNotAfterPickup } from './operating-hours';
import { RecurrencePatternVersion } from './recurrence';
import {
  AvailabilityTimeFrame,
  MAX_FLEXIBLE_DURATION_MINUTES,
  isDurationBasedTimeFrame,
  isWithinFlexibleDurationLimit,
} from './schedule-time-frame';

// A time of day, "HH:mm" 24h (e.g. "07:00").
const TimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/**
 * When a resource is open, for one date range and one set of weekdays.
 *
 * A resource keeps several of these — a summer range and a winter range, each able to read differently
 * on weekends — so the hours are rows rather than a single window on the rule. `until` open-ended means
 * the season runs on indefinitely; the recurrence pattern names ISO weekdays with Monday = 1, exactly as
 * a schedule row does, and is null for a window covering its start date alone.
 *
 * The two times are the day's guidelines: the resource opens at `opensAt` and closes at `closesAt`,
 * both on the same date, so a booking starts and ends between them. A row with neither belongs to a
 * resource booked in whole days.
 */
export const OperatingHoursSchema = z.object({
  // The latest a booking may end, on the same day it started. Null for a resource booked in whole
  // days: its availability is dates and weekdays only.
  closesAt: TimeOfDaySchema.nullable().default(null),
  // Set on a stored row; where windows overlap on a date, the earliest decides it. Never sent in.
  createdAt: z.string().optional(),
  // The resource these hours apply to. Null covers every resource in the group, which is what an
  // operator means when the whole group keeps the same hours.
  experienceId: z.string().uuid().nullable().default(null),
  id: z.string().uuid().optional(),
  // The earliest a booking may start.
  opensAt: TimeOfDaySchema.nullable().default(null),
  /**
   * Which weekdays the window repeats on, stored the way the schedule table stores recurrence.
   *
   * Null covers the start date alone — a window an operator added for one day, with nothing repeating.
   */
  recurrencePattern: z
    .object({
      version: z.nativeEnum(RecurrencePatternVersion),
      weekdays: z.array(z.number().int().min(1).max(7)).min(1),
    })
    .nullable()
    .default(null),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  until: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
});
export type OperatingHours = z.infer<typeof OperatingHoursSchema>;

// Durations cross the boundary as whole minutes (the established convention, e.g. sessionDurationMinutes);
// the API converts them to Postgres INTERVALs on the availability_rule row. This is the RESPONSE shape:
// a persisted rule always has a timeFrame.
const AvailabilityRuleFieldsSchema = z.object({
  // How often a booking may begin: 9:00, 9:15, 9:30 for a 15 minute increment.
  bookingIncrementMinutes: z.number().int().min(1),
  // The step between the lengths a guest may book: 1 hour, 2 hours, 3 hours for a 60 minute increment.
  // Null for a frame with no duration ladder — whole days, or a fixed session.
  durationIncrementMinutes: z.number().int().min(1).nullable(),
  // When the resource is open. Empty means no restriction beyond the duration rules.
  operatingHours: z.array(OperatingHoursSchema).default([]),
  maxDurationMinutes: z.number().int().min(1).nullable(),
  minDurationMinutes: z.number().int().min(1),
  timeFrame: z.nativeEnum(AvailabilityTimeFrame),
});

/**
 * A stated ceiling has to make sense: above the floor, and for a flexible resource, under a day.
 *
 * The ceiling itself is optional — an operator who does not cap a booking's length has not made a
 * mistake. Booking a flexible resource that states no ceiling still applies the day guardrail as one,
 * so a length can never run past a day whether or not anyone wrote it down. Daily and nightly frames
 * are counted in whole days and are exempt from that cap entirely.
 *
 * Enforced on the contract rather than only in the builder so every writer is held to it — the resource
 * group endpoints, a script, or an integration.
 */
const refineDurationGuardrail = (
  rule: {
    durationIncrementMinutes: number | null;
    maxDurationMinutes: number | null;
    minDurationMinutes: number;
    operatingHours: readonly OperatingHours[];
    timeFrame: AvailabilityTimeFrame | null;
  },
  ctx: z.RefinementCtx,
): void => {
  rule.operatingHours.forEach((hours, index) => {
    if (isDailyReturnNotAfterPickup(rule.timeFrame, hours)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: DAILY_RETURN_AFTER_PICKUP_MESSAGE,
        path: ['operatingHours', index, 'closesAt'],
      });
    }
  });

  if (rule.maxDurationMinutes != null && rule.maxDurationMinutes < rule.minDurationMinutes) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'maxDurationMinutes cannot be shorter than minDurationMinutes',
      path: ['maxDurationMinutes'],
    });
  }

  // An increment longer than the maximum would offer a first bookable length nobody could book.
  if (
    rule.durationIncrementMinutes != null &&
    rule.maxDurationMinutes != null &&
    rule.durationIncrementMinutes > rule.maxDurationMinutes
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'durationIncrementMinutes cannot be longer than maxDurationMinutes',
      path: ['durationIncrementMinutes'],
    });
  }

  if (!isDurationBasedTimeFrame(rule.timeFrame)) {
    return;
  }

  if (rule.maxDurationMinutes != null && !isWithinFlexibleDurationLimit(rule.maxDurationMinutes)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `maxDurationMinutes must be at most ${MAX_FLEXIBLE_DURATION_MINUTES} minutes for a flexible resource`,
      path: ['maxDurationMinutes'],
    });
  }

  if (!isWithinFlexibleDurationLimit(rule.minDurationMinutes)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `minDurationMinutes must be at most ${MAX_FLEXIBLE_DURATION_MINUTES} minutes for a flexible resource`,
      path: ['minDurationMinutes'],
    });
  }
};

export const AvailabilityRuleSchema = AvailabilityRuleFieldsSchema.superRefine(refineDurationGuardrail);
export type AvailabilityRule = z.infer<typeof AvailabilityRuleSchema>;

// Request/builder shape: timeFrame may be null while a continuous group is being configured (the
// builder gates publish until it's chosen, and the API only persists a rule once it is set).
export const AvailabilityRuleInputSchema = AvailabilityRuleFieldsSchema.extend({
  timeFrame: z.nativeEnum(AvailabilityTimeFrame).nullable(),
}).superRefine(refineDurationGuardrail);
export type AvailabilityRuleInput = z.infer<typeof AvailabilityRuleInputSchema>;
